import cx from "classnames";
import { FC, ReactNode, useContext, useId } from "react";
import {
  DEFAULT_MAP_STYLE_BASE_MAP,
  DEFAULT_MAP_STYLE_COUNTY_SHADING,
  DEFAULT_MAP_STYLE_SOLID_FILL_COLOR,
  MultiMapExportAction,
  MultiMapExportState,
} from "@app/projects/network-assessment/multi-map-export/state-management/multiMapExportStateManagement";
import {
  MAP_STYLE_ADEQUACY_SHADING_OPTIONS,
  MAP_STYLE_BASE_MAP_OPTIONS,
  MAP_STYLE_COUNTY_SHADING_OPTIONS,
  MAP_STYLE_COUNTY_STATUS_SHADING_OPTIONS,
  MAP_STYLE_SOLID_FILL_OPTIONS,
} from "@app/projects/network-assessment/multi-map-export/utils/mapStyleOptions";
import {
  MultiMapExportBaseMap,
  MultiMapExportCountyShading,
  MultiMapExportMapStyleOption,
  MultiMapExportShadingSwatch,
} from "@app/projects/network-assessment/multi-map-export/interfaces";
import { MultiMapExportContext } from "@app/projects/network-assessment/multi-map-export/MultiMapExportContext";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { SwatchLabel } from "@app/projects/network-assessment/multi-map-export/components/SwatchLabel";

const TEST_ID = "multi-map-export__map-style";

// Each radio group needs its own name, or selecting in one would clear the others.
const BASE_MAP_RADIO_NAME = "multi-map-export-map-style-base-map";
const COUNTY_SHADING_RADIO_NAME = "multi-map-export-map-style-county-shading";
const SOLID_FILL_RADIO_NAME = "multi-map-export-map-style-solid-fill";

/** Section 6's County Shading control. Only the selected mode's options are shown. */
export const CountyShadingGroup: FC<React.PropsWithChildren<unknown>> = () => {
  const { selections, selectionsChanged } = useContext(MultiMapExportContext);
  const countyShadingLabelId = useId();
  const fillColorLabelId = useId();

  const selectedCountyShading = selections?.selectedCountyShading ?? DEFAULT_MAP_STYLE_COUNTY_SHADING;
  const selectedSolidFillColor = selections?.selectedSolidFillColor ?? DEFAULT_MAP_STYLE_SOLID_FILL_COLOR;
  const selectedBaseMap = selections?.selectedBaseMap ?? DEFAULT_MAP_STYLE_BASE_MAP;

  const mapStyleChanged = (value: MultiMapExportState) => selectionsChanged?.({
    type: MultiMapExportAction.MapStyleChanged,
    value,
  });

  const renderLegend = <TValue extends string>(testIdSuffix: string, options: readonly MultiMapExportShadingSwatch<TValue>[]) => (
    <ul className="multi-map-export__county-shading-legend" data-testid={`${TEST_ID}-${testIdSuffix}`}>
      {options.map((option) => (
        <li key={option.value}>
          <SwatchLabel borderColor={option.borderColor} color={option.color} iconName={QesIcons.Square} label={option.label} />
        </li>
      ))}
    </ul>
  );

  const renderSolidFill = () => (
    <>
      <span className="multi-map-export__county-shading-fill-label" id={fillColorLabelId}>Fill Color</span>
      <div aria-labelledby={fillColorLabelId} className="multi-map-export__solid-fill-swatches" role="radiogroup">
        {MAP_STYLE_SOLID_FILL_OPTIONS.map((option) => {
          const id = `${TEST_ID}-solid-fill--${option.value}`;
          const isSelected = selectedSolidFillColor === option.value;

          return (
            <span key={option.value}>
              <input
                aria-label={option.label}
                checked={isSelected}
                className="visually-hidden multi-map-export__solid-fill-input"
                data-testid={id}
                id={id}
                name={SOLID_FILL_RADIO_NAME}
                onChange={() => mapStyleChanged({ selectedSolidFillColor: option.value })}
                type="radio"
                value={option.value}
              />
              <label
                className={cx("multi-map-export__solid-fill-swatch", { "multi-map-export__solid-fill-swatch--selected": isSelected })}
                htmlFor={id}
                style={{ backgroundColor: option.color }}
                title={option.label}
              />
            </span>
          );
        })}
      </div>
      <span className="multi-map-export__field-helper-text multi-map-export__county-shading-helper">Selected county uses the fill color above.</span>
    </>
  );

  const renderBaseMapOption = (option: MultiMapExportMapStyleOption<MultiMapExportBaseMap>) => (
    <QESRadioButton
      checked={selectedBaseMap === option.value}
      key={option.value}
      label={option.label}
      name={BASE_MAP_RADIO_NAME}
      onChange={() => mapStyleChanged({ selectedBaseMap: option.value })}
      testId={`${TEST_ID}-base-map--${option.value}`}
      value={option.value}
    />
  );

  const renderOptions = (shading: MultiMapExportCountyShading): ReactNode => {
    switch (shading) {
      case MultiMapExportCountyShading.SolidColor:
        return renderSolidFill();
      case MultiMapExportCountyShading.AdequacyStatus:
        return (
          <>
            <span className="multi-map-export__field-helper-text multi-map-export__county-shading-helper">
              Selected county is filled by its Adequacy Status for that map&apos;s specialty.
            </span>
            {renderLegend("adequacy-shading", MAP_STYLE_ADEQUACY_SHADING_OPTIONS)}
          </>
        );
      case MultiMapExportCountyShading.CountyStatus:
        return (
          <>
            <span className="multi-map-export__field-helper-text multi-map-export__county-shading-helper">Selected county is filled by its County Status.</span>
            {renderLegend("county-status-shading", MAP_STYLE_COUNTY_STATUS_SHADING_OPTIONS)}
            <span className="multi-map-export__field-helper-text multi-map-export__county-shading-helper">
              County Status is set by the client on the project service area.
            </span>
          </>
        );
      case MultiMapExportCountyShading.NoShading:
        return (
          <div aria-label="Background" role="radiogroup">
            {MAP_STYLE_BASE_MAP_OPTIONS.map(renderBaseMapOption)}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      aria-labelledby={countyShadingLabelId}
      className="multi-map-export__map-style-group multi-map-export__map-style-group--county-shading"
      data-testid={`${TEST_ID}-county-shading`}
      // A group rather than a radiogroup, because each mode nests its own options under it.
      role="group"
    >
      <span className="multi-map-export__field-label" id={countyShadingLabelId}>County Shading</span>
      {MAP_STYLE_COUNTY_SHADING_OPTIONS.map((option) => (
        <div key={option.value}>
          <QESRadioButton
            checked={selectedCountyShading === option.value}
            label={option.label}
            name={COUNTY_SHADING_RADIO_NAME}
            onChange={() => mapStyleChanged({ selectedCountyShading: option.value })}
            testId={`${TEST_ID}-county-shading--${option.value}`}
            value={option.value}
          />
          {selectedCountyShading === option.value && (
            <div className="multi-map-export__county-shading-options" data-testid={`${TEST_ID}-county-shading-options--${option.value}`}>
              {renderOptions(option.value)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CountyShadingGroup;
